/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable, inject } from '@angular/core';

import {
    Delivery,
    Equipment,
    Fai,
    FaimUnlimited,
    Insurance,
    Phone,
    Plan,
    Product,
    ProductFactory,
    PromotionModel,
    QuoteModel,
    Subscription,
    TYPE_MARKETING,
} from '@bytel/bytel-sales';
import { CheckoutCartModel, Oauth2Service } from '@bytel/checkout';

import { MainCartService } from '@services/main-cart.service';
import { OptionsService } from '@services/options.service';

import { ACCESSORIES_TYPES, FAI_ACCESSORIES } from '@interfaces/accessories.interface';

import { BasicObject } from '../interfaces/checkout.interface';
import { MobileRecoveryModel } from '../models/mobile-recovery.model';
import { CartTags, ProductTags, Tags } from '../shared/tag/tag.interface';
import { TagService } from '../shared/tag/tag.service';
import { Ftth3ClicsService } from './ftth3-clics.service';
import { MixedCartService } from './mixed-cart.service';
import { MobileRecoveryService } from './mobile-recovery.service';
import { PtoService } from './pto.service';

enum UNIVERS_VALUES {
    MOBILE = 'MOBILE',
    FAI = 'FAI',
    MIXTE = 'MIXTE',
}

const SIMO_TYPE_ID = 'plan_simo';

@Injectable({
    providedIn: 'root',
})
export class TagGeneratorServices {
    private ptoService: PtoService = inject(PtoService);
    public constructor(
        public cartService: MainCartService,
        protected oauth2: Oauth2Service,
        protected tagService: TagService,
        private mobileRecoveryService: MobileRecoveryService,
        private ftth3ClicsService: Ftth3ClicsService,
        private mixedCartService: MixedCartService,
    ) {}

    public getTags(page: string, etapeEcommerce: string, additionnalTags?: BasicObject): Tags {
        const quote: QuoteModel = this.cartService.getQuote();
        const principalSubscription: Product = quote.getPrincipalProduct<Subscription>('Subscription');
        const tags: Tags = this._buildTagsBase(page, etapeEcommerce, additionnalTags);
        if (!this.cartService.cartModel.isCartEmpty()) {
            tags.panier = this._buildCartTags(principalSubscription, page);
        }
        return tags;
    }

    private _buildCartTags(principalSubscription: Product, page: string): CartTags {
        let productsTags: Partial<ProductTags>[] = [];
        let hasInsurance: boolean = false;
        let hasPortaActive: boolean = false;
        let equipment: Equipment;
        this.cartService.cartModel.quotes.forEach((quote) => {
            quote.products.forEach((product: Product) => {
                productsTags.push(this._buildProductTags(product));

                if (product.prices.base - product.prices.final > 0) {
                    productsTags = [...productsTags, ...this._buildPromotionsTags(product)];
                }

                if (quote.getPrincipalProduct<Insurance>('Insurance')) {
                    hasInsurance = true;
                }

                if (quote.forms?.portability?.active) {
                    hasPortaActive = true;
                }

                equipment = quote.getPrincipalProduct<Equipment>('Equipment');
            });
        });

        productsTags = this._addMobileRecovery(productsTags);
        const cartTags: CartTags = {
            idPanier: `${this.cartService.cartModel.cartId}`,
            total: {
                base: this.cartService.cartModel.totals?.daily.base,
                recurring: this.cartService.cartModel.totals?.monthly.base,
            },
            type: principalSubscription ? principalSubscription.data.type_id : equipment?.data.type_id,
            canalVente: 'WEB',
            produits: productsTags,
            assurance: hasInsurance,
            portabilite: hasPortaActive,
        };
        if (page === 'Livraison' || page === 'Paiement') {
            const delivery: Delivery[] = [];
            delivery.push(
                ...this.cartService.cartModel.quotes
                    .map((quote) => quote.getProductsByType<Delivery>('Delivery'))
                    .flat(),
            );
            if (delivery.length > 0) {
                cartTags.modeLivraison = delivery[0].data?.sku;
            }
        }
        return cartTags;
    }

    private _addMobileRecovery(productTags: Partial<ProductTags>[]): Partial<ProductTags>[] {
        const quote: QuoteModel = this.cartService.getQuote();
        const mobileRecovery: MobileRecoveryModel = this.mobileRecoveryService.getRecoveryForQuote(quote);
        if (mobileRecovery && mobileRecovery.isCompleted()) {
            const name: string = `Reprise mobile ${mobileRecovery.getProductLabel()}`;
            productTags.push({
                id: null,
                name,
                gencode: name,
                sku: name,
                type_id: 'reprise_mobile',
                price: mobileRecovery.recovery.estimation.amount * -1,
                ligneProduit_niv1: 'Reprise_mobile',
            });
        }
        return productTags;
    }

    private _buildPromotionsTags(product: Product): Partial<ProductTags>[] {
        const promotionsTags: Partial<ProductTags>[] = [];
        const promotions: PromotionModel[] = [].concat(product.promotions.automatic, product.promotions.manual);
        if (product.promotions.coupon) {
            promotions.push(product.promotions.coupon);
        }

        promotions.forEach((promo: PromotionModel) => {
            const name: string = `Remise ${promo.name}`;
            let id: string;

            if (promo.typesMarketing.includes(TYPE_MARKETING.FOYER)) {
                id = product.name;
            } else {
                id = promo.code || promo.name;
            }

            promotionsTags.push({
                id,
                name,
                gencode: name,
                sku: name,
                price: promo.reductionAmount * -1,
                type_id: 'remise',
                ligneProduit_niv1: 'Promo',
            });
        });

        return promotionsTags;
    }

    private _buildProductTags(product: Product): ProductTags {
        const extraProductInCart = JSON.parse(sessionStorage.getItem('provider_pack_product') ?? '{}');
        const productTags: ProductTags = {
            name: product.name,
            id: product.data.entity_id.toString(),
            gencode: product.gencode,
            sku: product.data.sku,
            price: product.data.price,
            ligneProduit_niv1: product.productLine,
            ligneProduit_niv2: product.productLine === 'FAI' ? extraProductInCart.name : '',
            type_id: product.data.type_id,
            is_pro: product.isPro,
        };

        switch (true) {
            case ProductFactory.Is(product, FaimUnlimited):
                const productFaimUnlimited = product as FaimUnlimited;
                productTags.range = productFaimUnlimited.data.commercial_series;
                productTags.gamme = productFaimUnlimited.data.commercial_series;
                productTags.network = productFaimUnlimited.data.commercial_series;
                break;

            case ProductFactory.Is(product, Fai):
                const productFai = product as Fai;
                productTags.range = productFai.data.commercial_series;
                productTags.gamme = productFai.data.range_ng;
                productTags.technology = productFai.technology;
                productTags.degroupage = productFai.data.unbundling;
                productTags.network = productFai.data.network;
                productTags.play = productFai.play;
                break;

            case ProductFactory.Is(product, Phone):
                const phone = product as Phone;
                productTags.color = phone.data.color;
                productTags.capacity = phone.data.capacity;
                productTags.manufacturer = phone.data.manufacturer;
                break;

            case ProductFactory.Is(product, Plan):
                this._setProductTagsForPlan(product, this.cartService.cartModel, productTags);
                break;
        }

        return productTags;
    }

    private _setProductTagsForPlan(product: Product, cartModel: CheckoutCartModel, productTags: ProductTags): void {
        productTags.gamme = product.gamme;
        productTags.type_forfait = OptionsService.HasBlockedOption(cartModel) ? 'bloque' : 'non_bloque';

        if (product.categories?.includes(SIMO_TYPE_ID)) {
            this._setProductTagsForSimo(productTags);
        }
    }

    private _setProductTagsForSimo(productTags: ProductTags): void {
        productTags.type_id = SIMO_TYPE_ID;
        productTags.gamme = 'Sensation_SIM_seule';
    }

    private _buildTagsBase(page: string, etapeEcommerce: string, additionalTags: BasicObject): Tags {
        const quote: QuoteModel = this.cartService.getQuote();
        const isFai: boolean = quote.isAcquisitionFix();
        const isMixedCart = this._getIsMixedCart();
        const accessoriesQuote: boolean = this.cartService.hasOnlyAccessories(quote);
        let univers: string;
        let ligneMarche: string;

        switch (true) {
            case accessoriesQuote:
                const onlyFaiAccessories = quote.products.every((prod) =>
                    FAI_ACCESSORIES.includes(prod.data.accessory_type as ACCESSORIES_TYPES),
                );

                univers = onlyFaiAccessories ? UNIVERS_VALUES.FAI : UNIVERS_VALUES.MOBILE;
                ligneMarche = null;
                break;
            case isMixedCart:
                univers = UNIVERS_VALUES.MIXTE;
                ligneMarche = 'Mixte';
                break;
            case isFai:
                univers = UNIVERS_VALUES.FAI;
                ligneMarche = 'Fai';
                break;
            default:
                univers = UNIVERS_VALUES.MOBILE;
                ligneMarche = 'Mobile';
                break;
        }

        let variablesSpecifiques: BasicObject = {
            ftth3clics: this.ftth3ClicsService.isActive(),
            portabilite: quote?.forms?.portability?.active,
        };

        let pageVariante: BasicObject = {
            typeCommande: quote.isPreorder() ? 'Precommande' : 'Commande',
        };

        if (this.ptoService.ptoChoice) {
            variablesSpecifiques.choixPto = this.ptoService.ptoChoice;
        }

        {
            if (quote.isRenewal()) {
                variablesSpecifiques.changementOffre = this.cartService.isMigratingPlan();
            }
        }

        if (additionalTags?.variablesSpecifiques) {
            variablesSpecifiques = {
                ...variablesSpecifiques,
                ...additionalTags.variablesSpecifiques,
            };

            delete additionalTags.variablesSpecifiques;
        }

        if (additionalTags?.pageVariante) {
            pageVariante = {
                ...pageVariante,
                ...additionalTags.pageVariante,
            };

            delete additionalTags.pageVariante;
        }

        return {
            pageCategorie: 'Commande',
            pageVariante,
            page,
            parcours: quote.isRenewal() ? 'Renouvellement' : 'Acquisition',
            univers: `Boutique_${univers}`,
            etapeEcommerce,
            ligneMarche,
            activiteB2BouB2C: this.cartService.cartModel.isPro ? 'B2B' : 'B2C',
            variablesSpecifiques,
            ...additionalTags,
        };
    }

    private _getIsMixedCart(): boolean {
        return this.mixedCartService.isMixedCart(this.cartService.cartModel);
    }
}
